import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Skeleton, Result, Button, Divider, Space } from 'antd';
import { MessageOutlined, UserOutlined } from '@ant-design/icons';
import AuthShell from '../../ui/AuthShell';
import Card from '../../ui/Card';
import AuthHeader from '../../ui/AuthHeader';
import { decryptLinkPayload, getEncryptionKey } from '../../utils/encryption';
import AppStoreLinks from '../../components/AppStoreLinks';

const { Title, Text } = Typography;

const decryptChatLink = async (encryptedPart, key) => {
    try {
        const decryptedText = await decryptLinkPayload(encryptedPart, key);
        if (!decryptedText) throw new Error('Decryption returned empty.');

        const parts = decryptedText.split('||');
        if (parts.length < 2) throw new Error('Invalid payload structure.');

        return {
            id: parts[0],
            senderName: parts[1],
        };
    } catch (error) {
        return { error: error.message || error.toString() };
    }
};

const ChatPublicDetails = () => {
    const { id: routeId } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [showData, setShowData] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearTimeout(timeoutId);
                if (isMounted) setShowData(true);
            }
        };

        const loadData = async () => {
            if (!routeId) {
                setLoading(false);
                if (isMounted) setShowData(true);
                return;
            }

            const key = getEncryptionKey();
            const resultData = await decryptChatLink(routeId, key);

            if (!isMounted) return;

            setData(resultData);
            setLoading(false);

            if (resultData && resultData.error) {
                setShowData(true);
                return;
            }

            if (resultData && resultData.id) {
                document.addEventListener('visibilitychange', handleVisibilityChange);
                // Attempt to open the app via deep link immediately
                const appUrl = `vitalstaf://chat/${resultData.id}`;
                window.location.href = appUrl;

                timeoutId = setTimeout(() => {
                    if (isMounted) setShowData(true);
                }, 3000);
            } else {
                setShowData(true);
            }
        };

        loadData();

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [routeId]);

    if (loading) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px' }}>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </Card>
            </AuthShell>
        );
    }

    if (!showData) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Title level={4}>Opening in VitalStaf App...</Title>
                    <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
            </AuthShell>
        );
    }

    if (!data || data.error) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px' }}>
                    <Result
                        status="error"
                        title="Link Expired or Invalid"
                        subTitle={data && data.error ? `Error: ${data.error}` : "Sorry, we couldn't load the chat details for this link."}
                        extra={[
                            <Link key="home" to="/">
                                <Button type="primary" className="vs-btn vs-btn-primary">Go to Homepage</Button>
                            </Link>
                        ]}
                    />
                </Card>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <Card style={{ padding: 32, minWidth: '400px', maxWidth: '500px' }}>
                <AuthHeader
                    pillText="VitalStaf Message"
                    title="New Message"
                    subtitle="You have received a new message."
                />

                <Divider />

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <UserOutlined style={{ fontSize: 20, color: '#16a34a', marginRight: 16 }} />
                        <div>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>From</Text>
                            <Text strong>{data.senderName || 'Unknown Sender'}</Text>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <MessageOutlined style={{ fontSize: 20, color: '#16a34a', marginRight: 16 }} />
                        <div>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Message</Text>
                            <Text italic>Open the app or sign in to reply.</Text>
                        </div>
                    </div>
                </Space>

                <Divider style={{ margin: '32px 0 24px 0' }} />

                <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
                    <Button
                        type="default"
                        size="large"
                        block
                        className="vs-btn"
                        style={{ height: 48, fontSize: 16 }}
                        onClick={() => { window.location.href = `vitalstaf://chat/${data.id}`; }}
                    >
                        Open in VitalStaf App
                    </Button>

                    <Link to="/auth/login" style={{ width: '100%' }}>
                        <Button type="primary" size="large" block className="vs-btn vs-btn-primary" style={{ height: 48, fontSize: 16 }}>
                            Sign in to Reply
                        </Button>
                    </Link>
                </Space>
                <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">
                        Don't have an account? <Link to="/auth/signup">Sign up</Link>
                    </Text>
                </div>
                <AppStoreLinks />
            </Card>
        </AuthShell>
    );
};

export default ChatPublicDetails;
